import { BulkReviewActionResponse } from "../common/ObjectTypes";

/**
 * What a bulk action's summary should be reported as. Carries a translation key
 * and its variables rather than a finished string so `t()` does the
 * interpolation — matching how the AI Mappings table hands its partial-failure
 * key to `t()` instead of patching a translated string after the fact.
 */
export type BulkResultReport = {
  tone: "success" | "warning" | "info";
  key: string;
  vars: Record<string, number>;
};

/**
 * A skip reason an action can name in prose, paired with the approved sentence
 * that names it (AKL-811).
 *
 * A caller supplies one only when the sentence would be true of every skipped
 * photo — see `describeBulkResult` for the guard that enforces it.
 */
export type SkipReasonMessage = { reason: string; key: string };

/**
 * Point-to-one-species' skip sentence (AKL-811). The wording is approved
 * verbatim (2026-09-10) and needs fresh sign-off to change; the em dash is part
 * of it.
 */
export const pointToSpeciesSkip: SkipReasonMessage = {
  reason: "already_recorded",
  key: "{{updated}} photos updated. {{skipped}} left alone — a reviewer had already recorded observations.",
};

/**
 * Turn a bulk endpoint's summary into the toast the reviewer should see.
 *
 * Three outcomes, because reporting them the same way hides real information:
 *
 * - **Nothing skipped** — plain success naming how many photos changed.
 * - **Something skipped** — a warning naming the skipped count. An all-skipped
 *   response is still a 200, so the axios interceptor never fires for it and
 *   staying quiet would hide the outcome entirely. Skips are photos with nothing
 *   to do, not failures, which is why this is a warning and not an error.
 * - **Replayed** — a retry that landed on a batch which already committed. Its
 *   `applied_count` is 0 because *this* request wrote nothing; reporting that as
 *   "0 photos updated" reads as a broken button, so it gets its own message.
 *
 * An action that can say *why* it skipped photos passes a `skipMessage`
 * (AKL-811). That sentence is used only when **every** entry in `skipped`
 * carries its reason: it states the reason as a fact about the photos left
 * alone, and `reason` is typed `string` precisely because the API may add codes.
 * If an unknown one appears — a permission skip, a deleted asset — the specific
 * sentence would be a confident false statement about photos the web knows
 * nothing about, so the generic wording, which claims nothing about why, stands
 * instead. A clean batch keeps the plain success message either way, so the
 * sentence never renders as "0 left alone".
 *
 * The three AKL-753 callers pass no `skipMessage` and are unaffected.
 * @param summary the endpoint's response
 * @param skipMessage the reason/sentence pair this action can name, if any
 * @returns the tone, translation key and interpolation variables to toast
 */
export const describeBulkResult = (
  summary: BulkReviewActionResponse,
  skipMessage?: SkipReasonMessage,
): BulkResultReport => {
  if (summary.replayed) {
    return {
      tone: "info",
      key: "This action had already been applied to these photos.",
      vars: {},
    };
  }
  const skipped = summary.skipped.length;
  if (skipped === 0) {
    return {
      tone: "success",
      key: "{{count}} photos updated",
      vars: { count: summary.applied_count },
    };
  }
  if (
    skipMessage &&
    summary.skipped.every((s) => s.reason === skipMessage.reason)
  ) {
    return {
      tone: "warning",
      key: skipMessage.key,
      vars: { updated: summary.applied_count, skipped },
    };
  }
  return {
    tone: "warning",
    key: "{{applied}} photos updated. {{skipped}} were skipped because there was nothing to apply.",
    vars: { applied: summary.applied_count, skipped },
  };
};

/**
 * The message to show when a bulk action fails, or null when the axios
 * interceptor already handles it.
 *
 * The interceptor toasts network/timeout itself, redirects on a 5xx and signs
 * the user out on a 401, but **rejects 4xx without toasting**, so anything not
 * handled here would close the confirmation with no feedback at all —
 * indistinguishable from the button doing nothing. Three cases:
 *
 * - The action's own structured 422s (`batch_too_large`,
 *   `blank_taxon_not_allowed`), matched on `detail.error_code` and never on the
 *   bare status — these routes return other 422s, and "select fewer photos" for
 *   an unrelated validation failure would send the reviewer chasing the wrong
 *   thing.
 * - Any other 4xx, a 403 included — a generic failure message, so the attempt
 *   is never silent. A 403 is an ordinary refusal since AKL-939; the
 *   interceptor no longer clears the token or redirects for it.
 * - Everything else (5xx, network, 401) — null, because the interceptor has
 *   already toasted, redirected or thrown and a second message would double
 *   up. A 401 arrives as an `UnauthorizedError` with no `status`, so it is null
 *   by the first check; a normalized 401 is kept null too.
 * @param err the rejection from the axios wrapper (normalized `{status, data}`)
 * @returns a translation key to toast, or null to leave it to the interceptor
 */
export const bulkErrorMessageKey = (err: unknown): string | null => {
  const e = err as {
    status?: number;
    data?: { detail?: { error_code?: unknown } };
  };
  const status = e?.status;
  if (typeof status !== "number" || status < 400 || status >= 500) return null;
  // A 401 normally arrives as an UnauthorizedError (no status) and is null above; this
  // keeps the same silence if one ever arrives normalized. A 403 is deliberately NOT here
  // (AKL-939): it is an ordinary refusal and gets the generic message below.
  if (status === 401) return null;
  if (status === 422) {
    switch (e?.data?.detail?.error_code) {
      case "batch_too_large":
        return "Too many photos selected for one action. Select fewer and try again.";
      case "blank_taxon_not_allowed":
        return "Use Mark as blank to record blank photos, rather than adding a blank observation.";
      default:
        break;
    }
  }
  return "That action could not be applied to the selected photos. Please try again.";
};
